import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { userService } from '../services/userService';
import { networkService } from '../services/networkService';

const AuthContext = createContext({});

// Local storage keys for offline auth persistence
const LOCAL_AUTH_KEY = 'ez_bookmarks_auth_user';
const LOCAL_AUTH_TIMESTAMP = 'ez_bookmarks_auth_timestamp';
const AUTH_CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper functions for local auth storage
const saveLocalAuth = (user) => {
  try {
    if (user) {
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified
      };
      localStorage.setItem(LOCAL_AUTH_KEY, JSON.stringify(userData));
      localStorage.setItem(LOCAL_AUTH_TIMESTAMP, Date.now().toString());
      // console.log('💾 Saved auth state to localStorage');
    } else {
      localStorage.removeItem(LOCAL_AUTH_KEY);
      localStorage.removeItem(LOCAL_AUTH_TIMESTAMP);
      // console.log('🗑️ Cleared auth state from localStorage');
    }
  } catch (error) {
    console.error('❌ Failed to save auth state to localStorage:', error);
  }
};

const getLocalAuth = () => {
  try {
    const userData = localStorage.getItem(LOCAL_AUTH_KEY);
    const timestamp = localStorage.getItem(LOCAL_AUTH_TIMESTAMP);
    
    if (!userData || !timestamp) {
      // console.log('📱 No local auth data found');
      return null;
    }
    
    const age = Date.now() - parseInt(timestamp);
    if (age > AUTH_CACHE_DURATION) {
      // console.log('⏰ Local auth data expired, clearing...');
      localStorage.removeItem(LOCAL_AUTH_KEY);
      localStorage.removeItem(LOCAL_AUTH_TIMESTAMP);
      return null;
    }
    
    const user = JSON.parse(userData);
    // console.log('📱 Retrieved local auth data for user:', user.uid);
    return user;
  } catch (error) {
    console.error('❌ Failed to retrieve local auth data:', error);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [isOnline, setIsOnline] = useState(networkService.getNetworkStatus());

  useEffect(() => {
    // console.log('🔐 AuthContext: Setting up auth listener...');
    
    // Listen for network status changes
    const handleNetworkChange = (online) => {
      // console.log('🌐 Network status changed:', online ? 'online' : 'offline');
      setIsOnline(online);
    };
    
    const unsubscribeNetwork = networkService.addNetworkListener(handleNetworkChange);
    
    // If offline, try to restore from local storage immediately
    if (!networkService.getNetworkStatus()) {
      // console.log('📱 App is offline, trying to restore auth from localStorage...');
      const localUser = getLocalAuth();
      if (localUser) {
        // console.log('✅ Restored user from localStorage:', localUser.uid);
        setUser(localUser);
        setLoading(false);
        return;
      } else {
        // console.log('❌ No valid local auth data, waiting for network...');
      }
    }
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // console.log('🔐 AuthContext: Auth state changed, firebaseUser:', firebaseUser ? firebaseUser.uid : 'null');
      try {
        setError(null);
        if (firebaseUser) {
          // User is signed in
          // console.log('✅ User signed in:', firebaseUser.uid);
          
          // Save to local storage for offline access
          saveLocalAuth(firebaseUser);
          
          // Only try to save to Firestore if online
          if (networkService.getNetworkStatus()) {
            // console.log('💾 Attempting to save user to Firestore...');
            const userData = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              lastLoginAt: new Date()
            };
            
            try {
              const savedUser = await userService.saveUser(userData);
              // console.log('✅ User saved to Firestore successfully');
              
              // Show tutorial for new users who haven't seen it
              if (savedUser.isNewUser && !savedUser.hasSeenTutorial) {
                setShowTutorial(true);
              }
            } catch (firestoreError) {
              console.error('❌ Failed to save user to Firestore (but keeping user authenticated):', firestoreError);
              // Don't fail authentication just because Firestore save failed
            }
          } else {
            // console.log('📱 Offline - skipping Firestore operations');
          }
          
          // console.log('✅ Setting user state to:', firebaseUser.uid);
          setUser(firebaseUser);
        } else {
          // User is signed out
          // console.log('👋 User signed out');
          saveLocalAuth(null); // Clear local storage
          setUser(null);
          setShowTutorial(false);
        }
      } catch (err) {
        console.error('❌ Auth state change error:', err);
        setError('Authentication error occurred');
        
        // If offline and we have a cached user, use it
        if (!networkService.getNetworkStatus()) {
          const localUser = getLocalAuth();
          if (localUser) {
            // console.log('⚠️ Using cached user due to offline auth error:', localUser.uid);
            setUser(localUser);
          }
        } else if (firebaseUser) {
          // Online but error occurred - still set the user
          // console.log('⚠️ Setting user despite error to maintain auth');
          setUser(firebaseUser);
        }
      } finally {
        // console.log('🔐 AuthContext: Auth loading complete');
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
      unsubscribeNetwork();
    };
  }, []);

  const closeTutorial = async () => {
    setShowTutorial(false);
    if (user?.uid) {
      try {
        await userService.markTutorialSeen(user.uid);
      } catch (error) {
        console.error('Error marking tutorial as seen:', error);
      }
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isOnline,
    showTutorial,
    closeTutorial
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
